import { FC, useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import dayjs from 'dayjs'
import { comissaoService } from '@/services/comissaoService'
import { tenantService } from '@/services/tenantService'
import { authStore } from '@/store/authStore'
import { TenantAdmin } from '@/types/tenant'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { CurrencyField } from '@/components/common/CurrencyField'
import { cnpjValido, formatarCnpj, normalizarCnpj } from '@/utils/cnpj'
import { PoliticaNf, PoliticaNfResponse } from '@/types/comissao'
import { brl } from './estadoFinanceiro'
import { mensagemErroNota } from './errosNotaFiscal'
import { SeloPublicacao } from './SeloPublicacao'

const dataHora = (iso?: string | null) => (iso ? dayjs(iso).format('DD/MM/YYYY HH:mm') : '—')

interface Props {
  /** Propor nova versão: FINANCEIRO, GERENTE ou ADMIN (Q-AN9-3; V64). OPERADOR só lê. */
  podeEditar: boolean
  /** Aprovar/Rejeitar a proposta: FINANCEIRO ou ADMIN (V64, AN-PP6 §3.2) — e nunca quem propôs. */
  podeAprovar?: boolean
}

/**
 * Aba "Parâmetros da NF" do Financeiro (AN-9 §2): tolerância de conciliação (R$, absoluta —
 * Q-AN9-1) e CNPJ do tomador. Política versionada e append-only: salvar fecha a vigente e
 * abre uma nova versão, com motivo. Sem política, a tolerância é zero e a conferência abre
 * pendência "Parâmetros da NF incompletos" para o Financeiro.
 *
 * Q76 (AN-PP5 §7): a tolerância é ÚNICA (só na política global); o override por tenant define só
 * o CNPJ do tomador. O escopo "Tenant" não tem campo de tolerância e nunca a envia (o backend
 * responderia 422 `TOLERANCIA_POR_TENANT_PROIBIDA`).
 *
 * V64 (AN-PP6 §3): salvar PROPÕE (202 com `pendente`) — a tolerância/CNPJ em vigor não mudam até OUTRA pessoa
 * (FINANCEIRO/ADMIN) aprovar. A proposta aparece com Aprovar/Rejeitar, desabilitados para quem a propôs (o backend é
 * a trava: 403 `APROVACAO_PELO_PROPONENTE`). Erros (409 `PROPOSTA_JA_PENDENTE`, `CONFLITO_VERSAO`; 403
 * `APROVADOR_SEM_PAPEL`…) aparecem como o backend manda.
 */
const PoliticaNfPanel: FC<Props> = ({ podeEditar, podeAprovar = false }) => {
  const { enqueueSnackbar } = useSnackbar()
  const usuarioId = authStore((s) => s.usuario?.id)
  const [decidindo, setDecidindo] = useState(false)
  const [rejeitando, setRejeitando] = useState<PoliticaNf | null>(null)
  const [motivoRejeicao, setMotivoRejeicao] = useState('')
  const [escopo, setEscopo] = useState<'GLOBAL' | 'TENANT'>('GLOBAL')
  const [tenantId, setTenantId] = useState('')
  const [tenants, setTenants] = useState<TenantAdmin[] | null>(null)
  const [politica, setPolitica] = useState<PoliticaNfResponse | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<{ mensagem: string; status?: number } | null>(null)

  const [tolerancia, setTolerancia] = useState(0)
  const [cnpj, setCnpj] = useState('')
  const [motivo, setMotivo] = useState('')
  const [salvando, setSalvando] = useState(false)

  const tenantAlvo = escopo === 'TENANT' ? tenantId.trim() : ''

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      const p = await comissaoService.politicaNf(tenantAlvo || undefined)
      setPolitica(p)
      setTolerancia(p?.vigente?.toleranciaValor ?? p?.toleranciaValor ?? 0)
      setCnpj(p?.vigente?.cnpjTomador ?? (tenantAlvo ? '' : (p?.cnpjTomador ?? '')))
    } catch (e) {
      setErro({
        mensagem: (e as { message?: string })?.message ?? 'Falha ao carregar a política.',
        status: (e as { status?: number })?.status,
      })
    } finally {
      setCarregando(false)
    }
  }, [tenantAlvo])

  useEffect(() => {
    // Escopo tenant sem tenant escolhido: nada a carregar ainda.
    if (escopo === 'TENANT' && !tenantAlvo) return
    void carregar()
  }, [carregar, escopo, tenantAlvo])

  // Lista de tenants (melhor esforço; o endpoint é do ADMIN). Sem ela, o campo vira texto (id).
  useEffect(() => {
    if (escopo !== 'TENANT' || tenants !== null) return
    tenantService
      .listar()
      .then((lista) => setTenants(lista ?? []))
      .catch(() => setTenants([]))
  }, [escopo, tenants])

  const cnpjNormalizado = normalizarCnpj(cnpj)
  const cnpjInvalido = !!cnpjNormalizado && !cnpjValido(cnpjNormalizado)
  const podeSalvar =
    !salvando &&
    !!motivo.trim() &&
    !cnpjInvalido &&
    (escopo === 'GLOBAL' ? tolerancia >= 0 : !!tenantAlvo && !!cnpjNormalizado)

  const salvar = async () => {
    setSalvando(true)
    try {
      const p = await comissaoService.salvarPoliticaNf(
        escopo === 'GLOBAL'
          ? { toleranciaValor: tolerancia, cnpjTomador: cnpjNormalizado || null, motivo: motivo.trim() }
          : { tenantId: tenantAlvo, toleranciaValor: null, cnpjTomador: cnpjNormalizado || null, motivo: motivo.trim() }
      )
      enqueueSnackbar(
        `Proposta${p?.pendente ? ` (versão ${p.pendente.versao})` : ''} enviada para aprovação: os parâmetros em vigor só mudam depois que outra pessoa aprovar.`,
        { variant: 'success' }
      )
      setMotivo('')
      if (p) setPolitica(p)
      await carregar()
    } catch (e) {
      enqueueSnackbar(mensagemErroNota(e, 'Falha ao salvar os parâmetros da NF.'), { variant: 'error' })
    } finally {
      setSalvando(false)
    }
  }

  const aprovar = async (pendente: PoliticaNf) => {
    setDecidindo(true)
    try {
      const p = await comissaoService.aprovarPoliticaNf(pendente.id, pendente.versaoRegistro ?? null)
      enqueueSnackbar(`Versão ${pendente.versao} aprovada: passa a valer agora.`, { variant: 'success' })
      if (p) setPolitica(p)
      await carregar()
    } catch (e) {
      enqueueSnackbar(mensagemErroNota(e, 'Falha ao aprovar a proposta.'), { variant: 'error' })
    } finally {
      setDecidindo(false)
    }
  }

  /** PP-final: o proponente retira a própria proposta — libera o escopo para uma nova (como na simulação). */
  const retirar = async (pendente: PoliticaNf) => {
    setDecidindo(true)
    try {
      const p = await comissaoService.retirarPoliticaNf(pendente.id)
      enqueueSnackbar(`Proposta (versão ${pendente.versao}) retirada; fica no histórico como rejeitada.`, {
        variant: 'success',
      })
      if (p) setPolitica(p)
      await carregar()
    } catch (e) {
      enqueueSnackbar(mensagemErroNota(e, 'Falha ao retirar a proposta.'), { variant: 'error' })
    } finally {
      setDecidindo(false)
    }
  }

  const rejeitar = async () => {
    if (!rejeitando) return
    setDecidindo(true)
    try {
      const p = await comissaoService.rejeitarPoliticaNf(
        rejeitando.id,
        motivoRejeicao.trim(),
        rejeitando.versaoRegistro ?? null
      )
      enqueueSnackbar(`Proposta (versão ${rejeitando.versao}) rejeitada.`, { variant: 'success' })
      setRejeitando(null)
      if (p) setPolitica(p)
      await carregar()
    } catch (e) {
      enqueueSnackbar(mensagemErroNota(e, 'Falha ao rejeitar a proposta.'), { variant: 'error' })
    } finally {
      setDecidindo(false)
    }
  }

  const seletorEscopo = (
    <Card>
      <CardContent>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField
            select
            size="small"
            label="Escopo"
            value={escopo}
            onChange={(e) => setEscopo(e.target.value as 'GLOBAL' | 'TENANT')}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="GLOBAL">Global (tolerância e CNPJ)</MenuItem>
            <MenuItem value="TENANT">Tenant (só CNPJ do tomador)</MenuItem>
          </TextField>
          {escopo === 'TENANT' &&
            (tenants && tenants.length > 0 ? (
              <TextField
                select
                size="small"
                label="Tenant"
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                sx={{ minWidth: 260 }}
              >
                {tenants.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.nome}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                size="small"
                label="Tenant (id)"
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                sx={{ minWidth: 320 }}
              />
            ))}
        </Stack>
        <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
          Tolerância é única para todos os tenants (Q76): o override do tenant define só o CNPJ do tomador.
        </Typography>
      </CardContent>
    </Card>
  )

  if (escopo === 'TENANT' && !tenantAlvo) {
    return (
      <Stack spacing={2}>
        {seletorEscopo}
        <Typography variant="body2" color="textSecondary">
          Escolha o tenant para ver e alterar o CNPJ do tomador dele.
        </Typography>
      </Stack>
    )
  }

  let estadoCarga: JSX.Element | null = null
  if (carregando) estadoCarga = <EstadoDeLista estado="carregando" minHeight={160} />
  else if (erro) {
    estadoCarga =
      erro.status === 403 ? (
        <EstadoDeLista estado="semPermissao" minHeight={160} />
      ) : (
        <EstadoDeLista
          estado="erro"
          mensagem="Não foi possível carregar os parâmetros da NF"
          detalhe={erro.mensagem}
          acao={{ label: 'Tentar novamente', onClick: () => void carregar() }}
          minHeight={160}
        />
      )
  }
  if (estadoCarga) {
    return (
      <Stack spacing={2}>
        {seletorEscopo}
        {estadoCarga}
      </Stack>
    )
  }

  const vigente = politica?.vigente ?? null
  const historico = politica?.historico ?? []
  const pendente = politica?.pendente ?? null
  const doTenant = escopo === 'TENANT'
  const propostaPropria = !!pendente && !!usuarioId && pendente.propostoPor === usuarioId

  return (
    <Stack spacing={2}>
      {seletorEscopo}
      {vigente ? (
        <Card>
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Parâmetros em vigor
              </Typography>
              <Chip size="small" label={`versão ${vigente.versao}`} />
              {vigente.tenantId && <Chip size="small" variant="outlined" label="deste tenant" />}
            </Stack>
            {doTenant ? (
              <Typography variant="body2">
                CNPJ do tomador deste tenant:{' '}
                <strong>{vigente.cnpjTomador ? formatarCnpj(vigente.cnpjTomador) : 'não cadastrado'}</strong> ·
                Tolerância (global): <strong>{brl(politica?.toleranciaValor)}</strong>
              </Typography>
            ) : (
              <Typography variant="body2">
                Tolerância: <strong>{brl(vigente.toleranciaValor)}</strong> · CNPJ do tomador:{' '}
                <strong>{vigente.cnpjTomador ? formatarCnpj(vigente.cnpjTomador) : 'não cadastrado'}</strong>
              </Typography>
            )}
            <Typography variant="caption" color="textSecondary">
              Desde {dataHora(vigente.vigenciaInicio)} · {vigente.motivo}
            </Typography>
          </CardContent>
        </Card>
      ) : doTenant ? (
        <Alert severity="info">
          Este tenant não tem CNPJ do tomador próprio: vale o da política global. Tolerância (global):{' '}
          {brl(politica?.toleranciaValor)}.
        </Alert>
      ) : (
        <Alert severity="warning">
          Nenhum parâmetro cadastrado: a tolerância é zero (o valor da nota precisa bater
          exatamente com as comissões) e toda conferência abre a pendência “Parâmetros da NF
          incompletos” até o CNPJ do tomador ser cadastrado.
        </Alert>
      )}

      {pendente && (
        <Card variant="outlined" aria-label="Proposta aguardando aprovação">
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Proposta aguardando aprovação
              </Typography>
              <Chip size="small" label={`versão ${pendente.versao}`} />
              <SeloPublicacao politica={pendente} />
            </Stack>
            <Typography variant="body2">
              {doTenant ? null : (
                <>
                  Tolerância proposta:{' '}
                  <strong>{pendente.toleranciaValor != null ? brl(pendente.toleranciaValor) : '—'}</strong> ·{' '}
                </>
              )}
              CNPJ do tomador proposto:{' '}
              <strong>{pendente.cnpjTomador ? formatarCnpj(pendente.cnpjTomador) : 'não cadastrado'}</strong>
            </Typography>
            <Typography variant="body2">
              Proposta por <strong>{propostaPropria ? 'você' : (pendente.propostoPor ?? '—')}</strong> em{' '}
              {dataHora(pendente.criadoEm)} · Motivo: {pendente.motivo}
            </Typography>
            <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
              Não vale até outra pessoa (Financeiro ou administrador) aprovar; aprovada, vale a partir do instante da
              aprovação.
            </Typography>
            {podeAprovar && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  variant="contained"
                  disabled={decidindo || propostaPropria}
                  onClick={() => void aprovar(pendente)}
                >
                  Aprovar
                </Button>
                <Button
                  color="error"
                  disabled={decidindo || propostaPropria}
                  onClick={() => {
                    setMotivoRejeicao('')
                    setRejeitando(pendente)
                  }}
                >
                  Rejeitar
                </Button>
                {propostaPropria && (
                  <Button color="warning" disabled={decidindo} onClick={() => void retirar(pendente)}>
                    Retirar proposta
                  </Button>
                )}
                {propostaPropria && (
                  <Typography variant="caption" color="textSecondary">
                    Proposta sua: quem propõe não aprova nem rejeita (pode retirá-la para propor de novo).
                  </Typography>
                )}
              </Stack>
            )}
            {!podeAprovar && propostaPropria && (
              <Button color="warning" disabled={decidindo} onClick={() => void retirar(pendente)}>
                Retirar proposta
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {podeEditar ? (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Propor nova versão
            </Typography>
            {pendente && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Já há uma proposta pendente neste escopo: aprove ou rejeite antes de propor outra (o backend responde
                409 PROPOSTA_JA_PENDENTE).
              </Alert>
            )}
            <Stack spacing={2}>
              {!doTenant && (
                <CurrencyField
                  label="Tolerância (R$)"
                  value={tolerancia}
                  onChange={setTolerancia}
                  helperText="Diferença aceita entre o valor da nota e a soma das comissões. Única para todos os tenants."
                />
              )}
              <TextField
                label="CNPJ do tomador"
                value={cnpj}
                onChange={(e) => setCnpj(e.target.value)}
                error={cnpjInvalido}
                required={doTenant}
                helperText={cnpjInvalido ? 'CNPJ inválido.' : 'O CNPJ que deve constar como tomador nas notas.'}
              />
              <TextField
                label="Motivo da alteração"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                multiline
                minRows={2}
              />
              <Box>
                <Button variant="contained" onClick={() => void salvar()} disabled={!podeSalvar}>
                  {salvando ? 'Enviando…' : 'Enviar para aprovação'}
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <Typography variant="body2" color="textSecondary">
          Só Financeiro, gerente ou administrador propõem mudança nos parâmetros da NF.
        </Typography>
      )}

      {historico.length > 0 && (
        <Card>
          <TableContainer>
            <Table size="small" aria-label="Histórico dos parâmetros da NF">
              <TableHead>
                <TableRow>
                  <TableCell>Versão</TableCell>
                  <TableCell>Situação</TableCell>
                  <TableCell align="right">Tolerância</TableCell>
                  <TableCell>CNPJ do tomador</TableCell>
                  <TableCell>Vigência</TableCell>
                  <TableCell>Motivo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historico.map((h) => (
                  <TableRow key={h.id}>
                    <TableCell>{h.versao}</TableCell>
                    <TableCell>
                      {h.statusPublicacao === 'ATIVA' && !h.legado ? 'Aprovada' : null}
                      <SeloPublicacao politica={h} />
                      {h.statusPublicacao === 'REJEITADA' && h.motivoRejeicao && (
                        <Typography variant="caption" color="textSecondary" display="block">
                          {h.motivoRejeicao}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{h.toleranciaValor != null ? brl(h.toleranciaValor) : '—'}</TableCell>
                    <TableCell>{h.cnpjTomador ? formatarCnpj(h.cnpjTomador) : '—'}</TableCell>
                    <TableCell>
                      {h.statusPublicacao === 'PENDENTE_APROVACAO' || h.statusPublicacao === 'REJEITADA'
                        ? '—'
                        : `${dataHora(h.vigenciaInicio)} → ${h.vigenciaFim ? dataHora(h.vigenciaFim) : 'em vigor'}`}
                    </TableCell>
                    <TableCell>{h.motivo}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      <Dialog open={rejeitando !== null} onClose={() => setRejeitando(null)} fullWidth maxWidth="xs">
        <DialogTitle>Rejeitar proposta (versão {rejeitando?.versao})</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Motivo da rejeição"
            value={motivoRejeicao}
            onChange={(e) => setMotivoRejeicao(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            inputProps={{ maxLength: 500 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejeitando(null)}>Voltar</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void rejeitar()}
            disabled={decidindo || !motivoRejeicao.trim()}
          >
            {decidindo ? 'Rejeitando…' : 'Rejeitar proposta'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default PoliticaNfPanel
